import produce from "immer";
import { clamp } from "./statsHelper";
import { IBitAction, IBitActionExecutor, IEntity } from "./types";

const physicalAttack = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { attack: sourceAttack } = draft[sourceIndex].stats;
  const { hp, defense } = draft[targetIndex].stats;
  const { intensity = 1 } = action;
  hp.value -= (sourceAttack.value - defense.value) * intensity;
  clamp(hp);
  return true;
};

const attack = () => {
  return physicalAttack;
};

/**
 * Magic
 */

const heal = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { hp } = draft[targetIndex].stats;
  const { intensity = 1, cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  hp.value += intensity;
  clamp(mp);
  clamp(hp);
  return true;
};

const refresh = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = [];
  clamp(mp);
  return true;
};

const protect = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { statuses } = draft[targetIndex];
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== "protect")
    .concat({ key: "protect", start: 0, end: 5 });
  clamp(mp);
  return true;
};

const shell = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { statuses } = draft[targetIndex];
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== "shell")
    .concat({ key: "shell", start: 0, end: 5 });
  clamp(mp);
  return true;
};

const poison = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { statuses } = draft[targetIndex];
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== "poison")
    .concat({ key: "poison", start: 0, end: 5 });
  clamp(mp);
  return true;
};

const sleep = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { statuses } = draft[targetIndex];
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== "sleep")
    .concat({ key: "sleep", start: 0, end: 2 });
  clamp(mp);
  return true;
};

const blind = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { statuses } = draft[targetIndex];
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== "blind")
    .concat({ key: "blind", start: 0, end: 6 });
  clamp(mp);
  return true;
};

const elementalMagicAttack = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mpAttack: sourceMpAttack, mp } = draft[sourceIndex].stats;
  const { hp, mpDefense } = draft[targetIndex].stats;
  const { affinity, intensity = 1, cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;

  const affinityMultiplier =
    affinity !== undefined ? draft[targetIndex].affinities[affinity] : 1;
  hp.value -=
    (sourceMpAttack.value - mpDefense.value) * intensity * affinityMultiplier;
  clamp(hp);
  clamp(mp);
  return true;
};

// eslint-disable-next-line complexity
const magic = (action?: IBitAction) => {
  if (!action) return () => false;
  if (action.key === "heal") return heal;
  if (action.key === "protect") return protect;
  if (action.key === "shell") return shell;
  if (action.key === "refresh") return refresh;

  if (action.key === "poison") return poison;
  if (action.key === "sleep") return sleep;
  if (action.key === "blind") return blind;
  return elementalMagicAttack;
};

type IBitActionExecutorHOF = Record<
  IBitAction["type"],
  (action?: IBitAction) => IBitActionExecutor[IBitAction["key"]]
>;

export const actions: IBitActionExecutorHOF = { attack, magic };

export const act = (
  actors: IEntity[],
  sourceIndex: number,
  targetIndex: number,
  action: IBitAction
): IEntity[] => {
  return produce(actors, draft => {
    const executor = actions[action.type](action);
    const succeeded = executor(draft, { sourceIndex, targetIndex, action });
    draft[targetIndex].lastAction = { ...action, succeeded };
  });
};
